import type { ReactNode } from 'react'
import { CalendarSelectionModal } from 'src/modules/calendar/components'
import { FormattedText, InfoModal } from 'src/modules/core/components'
import type { PlanChangeStatus } from 'src/modules/payment/types/plans-offer.types'
import type { PlanChangeModalsProps } from './plan-change-modals.interface'

const strong = (chunks: ReactNode) => <strong>{chunks}</strong>

const STATUS_MESSAGE_IDS: Record<Exclude<PlanChangeStatus, 'success'>, { title: string; description: string }> = {
  error: {
    title: 'payment.subscription.change.error.title',
    description: 'payment.subscription.change.error.description',
  },
  trialError: {
    title: 'payment.subscription.change.trialError.title',
    description: 'payment.subscription.change.trialError.description',
  },
  canceledError: {
    title: 'payment.subscription.change.trialError.title',
    description: 'payment.subscription.change.canceledError.description',
  },
  pastDueError: {
    title: 'payment.subscription.change.trialError.title',
    description: 'payment.subscription.change.pastDueError.description',
  },
  pausedError: {
    title: 'payment.subscription.change.trialError.title',
    description: 'payment.subscription.change.pausedError.description',
  },
}

export function PlanChangeModals({ flow, currentPlanName }: PlanChangeModalsProps) {
  return (
    <>
      {flow.isCalendarSelectionModalOpen && (
        <CalendarSelectionModal
          subscriptionPlan={flow.newPlan}
          title={<FormattedText id="calendars.settings.modal.title" />}
          button={<FormattedText id="payment.subscription.change.confirm.button.change" />}
          closeButton={<FormattedText id="common.doNotMakeChanges" />}
          onButtonClick={flow.onSelectCalendars}
          onClose={flow.closeCalendarSelectionModal}
          selectFromExistingCalendars
          isNewSubscriptionPlan
        />
      )}
      {flow.isConfirmModalOpen && <ConfirmPlanChangeModal flow={flow} currentPlanName={currentPlanName} />}
      <PlanChangeStatusModal flow={flow} />
    </>
  )
}

function ConfirmPlanChangeModal({ flow, currentPlanName }: PlanChangeModalsProps) {
  const amount = flow.immediatePaymentAmount

  return (
    <InfoModal
      title={<FormattedText id="payment.subscription.change.confirm.title" />}
      description={
        <>
          <FormattedText
            id="payment.subscription.change.confirm.description"
            values={{ strong, currentPlan: currentPlanName, newPlan: flow.newPlan.name }}
          />{' '}
          {flow.isShopifyBilled ? (
            <FormattedText id="payment.subscription.change.confirm.shopify" />
          ) : (
            <FormattedText
              id={
                amount < 0
                  ? 'payment.subscription.change.confirm.negative'
                  : 'payment.subscription.change.confirm.positive'
              }
              values={{ strong, amount: Math.abs(amount) }}
            />
          )}
        </>
      }
      closeButton={<FormattedText id="common.doNotMakeChanges" />}
      button={
        <FormattedText
          id={
            flow.isShopifyBilled
              ? 'payment.subscription.change.confirm.button.shopify'
              : 'payment.subscription.change.confirm.button.change'
          }
        />
      }
      onClick={flow.handleChangeSubscription}
      onClose={flow.closeConfirmModal}
    />
  )
}

function PlanChangeStatusModal({ flow }: Pick<PlanChangeModalsProps, 'flow'>) {
  if (flow.status === 'success') {
    return (
      <InfoModal
        title={<FormattedText id="payment.subscription.change.success.title" />}
        description={
          <FormattedText
            id="payment.subscription.change.success.description"
            values={{ strong, plan: flow.newPlan.name }}
          />
        }
        button={<FormattedText id="common.ok" />}
        onClick={flow.closeStatus}
        onClose={flow.closeStatus}
      />
    )
  }

  const messageIds = STATUS_MESSAGE_IDS[flow.status]

  if (!messageIds) {
    return null
  }

  return (
    <InfoModal
      title={<FormattedText id={messageIds.title} />}
      description={<FormattedText id={messageIds.description} />}
      button={<FormattedText id="common.ok" />}
      onClick={flow.closeStatus}
      onClose={flow.closeStatus}
    />
  )
}
